import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Plus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { tr } from "@/lib/i18n";
import AuditionFormDialog from "@/components/crm/AuditionFormDialog";

const MM_FIELDS = [
  "current_feelings",
  "meditation_observation",
  "remembered_experience",
  "video_observation",
  "video_surprise",
  "what_worked",
  "what_to_improve",
  "lesson_learned",
  "behavior_pattern",
  "behavior_pattern_history",
  "current_challenge",
  "next_casting_training",
  "leave_behind",
];

const PRE_FIELDS = [
  "role_fit",
  "result_intuition",
  "challenge",
  "training_focus",
  "thing_to_face",
];

function emptyForm() {
  const f = {};
  for (const k of MM_FIELDS) f[k] = "";
  return f;
}

function hasAny(obj, keys) {
  if (!obj) return false;
  for (const k of keys) if (((obj[k] || "") + "").trim()) return true;
  return false;
}

function TitleBlock({ title, body }) {
  return (
    <div>
      <div className="mono text-[11px] tracking-[0.4em] text-amber-500 uppercase">
        MASTERMIND
      </div>
      <h1 className="serif text-4xl text-stone-100 mt-2">{title}</h1>
      {body && (
        <p className="text-stone-300 mt-4 whitespace-pre-line leading-relaxed">
          {body}
        </p>
      )}
    </div>
  );
}

function NavRow({ onNext, onBack, nextLabel, disableNext, extra, saving, t }) {
  return (
    <div className="flex items-center justify-between gap-3 flex-wrap pt-2">
      <button
        type="button"
        data-testid="mm-nav-back"
        onClick={onBack}
        className="ghost-btn"
        disabled={saving}
      >
        <ArrowLeft size={12} /> {t("mm.nav.back")}
      </button>
      <div className="flex items-center gap-2">
        {extra}
        {onNext && (
          <button
            type="button"
            data-testid="mm-nav-next"
            onClick={onNext}
            className="pill-btn"
            disabled={saving || disableNext}
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : null}
            {nextLabel || t("mm.nav.next")}
          </button>
        )}
      </div>
    </div>
  );
}

function Field({ testid, value, onChange, placeholder }) {
  return (
    <textarea
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      rows={5}
      className="w-full bg-stone-950 border border-stone-800 rounded-sm p-3 text-stone-100 text-sm focus:border-amber-500/60 outline-none"
      placeholder={placeholder || ""}
    />
  );
}

export default function MastermindNext() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const { lang, access } = useAuth();
  const t = (k) => tr(k, lang);
  const auditionParam = sp.get("audition") || "";

  // Plan gate: MASTERMIND is available only on start/monthly/annual. Users on
  // free/single/sprint are redirected to the reused Career Path preview paywall
  // (see /career/preview/mastermind). Backend endpoints enforce the same rule.
  useEffect(() => {
    if (access && !(access.has_access === true && ["start", "monthly", "annual", "light"].includes(access.plan))) {
      nav("/career/preview/mastermind", { replace: true });
    }
    // eslint-disable-next-line
  }, [access]);

  const [phase, setPhase] = useState(auditionParam ? "loading" : "select");
  const [audition, setAudition] = useState(null);
  const [auditions, setAuditions] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [step, setStep] = useState(0);      // 0 = intro, 1..10 = steps, 11 = final
  const [saving, setSaving] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  const loadAudition = async (id) => {
    try {
      const r = await api.get(`/crm/auditions/${id}`);
      const a = r.data;
      setAudition(a);
      const mr = a.mastermind_review || {};
      const next = { ...emptyForm() };
      for (const k of MM_FIELDS) next[k] = mr[k] || "";
      setForm(next);
      setPhase("intro");
      setStep(0);
    } catch {
      toast.error(t("aud.form.fail"));
      setPhase("select");
    }
  };

  const loadList = async () => {
    try {
      const [aRes, cRes] = await Promise.all([
        api.get("/crm/auditions"),
        api.get("/crm/contacts"),
      ]);
      setAuditions(aRes.data || []);
      setContacts(cRes.data || []);
    } catch {
      toast.error(t("aud.form.fail"));
    }
  };

  useEffect(() => {
    if (auditionParam) {
      loadAudition(auditionParam);
    } else {
      loadList();
    }
    // eslint-disable-next-line
  }, [auditionParam]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const saveField = async (fields) => {
    if (!audition) return false;
    setSaving(true);
    try {
      const payload = {};
      for (const k of fields) payload[k] = form[k] || "";
      const r = await api.patch(
        `/crm/auditions/${audition.audition_id}/mastermind`,
        payload,
      );
      setAudition(r.data);
      return true;
    } catch {
      toast.error(t("aud.form.fail"));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const goNext = async (fields) => {
    const ok = await saveField(fields);
    if (ok) setStep((s) => s + 1);
  };

  const goBack = () => setStep((s) => Math.max(0, s - 1));

  const skipStep3 = () => setStep(4);

  const closeAudition = async () => {
    const ok = await saveField(["leave_behind"]);
    if (!ok) return;
    setSaving(true);
    try {
      const r = await api.post(
        `/crm/auditions/${audition.audition_id}/mastermind/complete`,
      );
      setAudition(r.data);
      setStep(11);
    } catch {
      toast.error(t("aud.form.fail"));
    } finally {
      setSaving(false);
    }
  };

  const preCasting = audition?.pre_casting || {};
  const hasPreCasting = useMemo(
    () => hasAny(preCasting, PRE_FIELDS),
    [preCasting],
  );

  const summaryRows = (rows) =>
    rows
      .map(([labelKey, val]) => ({ labelKey, val: (val || "").trim() }))
      .filter((r) => r.val);

  // ---- Renderers (defined at module scope above to keep DOM identity stable
  // across renders; inline versions would remount the textarea on each keystroke). -----------------------------------------------------------

  // ---- Phase: loading / select --------------------------------------------

  if (phase === "loading") {
    return (
      <div className="mono text-stone-500 text-sm">{t("dash.loading")}</div>
    );
  }

  if (phase === "select") {
    return (
      <div className="max-w-3xl space-y-6" data-testid="mm-select">
        <div>
          <div className="mono text-[11px] tracking-[0.4em] text-amber-500 uppercase">
            MASTERMIND
          </div>
          <h1 className="serif text-4xl text-stone-100 mt-2">{t("mm.title")}</h1>
          <p className="text-stone-300 mt-3 italic">{t("mm.subtitle")}</p>
        </div>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="label-cap">{t("mm.select.title")}</div>
          <button
            type="button"
            data-testid="mm-add-audition"
            onClick={() => setAddOpen(true)}
            className="pill-btn"
          >
            <Plus size={14} /> {t("mm.select.add_new")}
          </button>
        </div>
        {auditions.length === 0 ? (
          <div className="paper p-6 text-stone-500 text-sm text-center">
            {t("aud.empty")}
          </div>
        ) : (
          <ul className="space-y-2">
            {auditions.map((a) => (
              <li key={a.audition_id}>
                <button
                  type="button"
                  data-testid={`mm-pick-${a.audition_id}`}
                  onClick={() => loadAudition(a.audition_id)}
                  className="w-full paper p-4 text-left hover:border-amber-500/50 transition-colors"
                >
                  <div className="serif text-lg text-stone-100">{a.project}</div>
                  <div className="text-xs text-stone-400 mt-1">
                    {a.role || "—"} · {t(`aud.status.${a.status || "new"}`)}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}

        <AuditionFormDialog
          open={addOpen}
          onOpenChange={setAddOpen}
          castingDirectors={contacts.filter(
            (c) => c.type === "casting_director",
          )}
          onCreated={(newA) => {
            setAddOpen(false);
            if (newA?.audition_id) loadAudition(newA.audition_id);
          }}
        />
      </div>
    );
  }

  // ---- Phase: intro & steps -----------------------------------------------

  const stepShell = (children) => (
    <div className="max-w-3xl space-y-6" data-testid={`mm-step-${step}`}>
      {audition && (
        <div className="mono text-[10px] tracking-widest text-stone-500 uppercase">
          {audition.project} {audition.role ? `· ${audition.role}` : ""}
        </div>
      )}
      {children}
    </div>
  );

  if (step === 0) {
    return stepShell(
      <>
        <TitleBlock title={t("mm.title")} body={t("mm.description")} />
        <p className="text-stone-300 italic">{t("mm.subtitle")}</p>
        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            data-testid="mm-nav-back"
            onClick={() => nav(-1)}
            className="ghost-btn"
          >
            <ArrowLeft size={12} /> {t("mm.nav.back")}
          </button>
          <button
            type="button"
            data-testid="mm-nav-next"
            onClick={() => setStep(1)}
            className="pill-btn"
          >
            {t("mm.nav.next")}
          </button>
        </div>
      </>,
    );
  }

  if (step === 1) {
    return stepShell(
      <>
        <TitleBlock title={t("mm.step1.title")} body={t("mm.step1.body")} />
        <div>
          <div className="label-cap mb-2">{t("mm.step1.question")}</div>
          <Field
            testid="mm-f-current_feelings"
            value={form.current_feelings}
            onChange={(v) => set("current_feelings", v)}
          />
        </div>
        <NavRow saving={saving} t={t} onNext={() => goNext(["current_feelings"])} onBack={goBack} />
      </>,
    );
  }

  if (step === 2) {
    return stepShell(
      <>
        <TitleBlock title={t("mm.step2.title")} body={t("mm.step2.body")} />
        <div>
          <div className="label-cap mb-2">{t("mm.step2.question")}</div>
          <Field
            testid="mm-f-meditation_observation"
            value={form.meditation_observation}
            onChange={(v) => set("meditation_observation", v)}
          />
        </div>
        <NavRow saving={saving} t={t}
          onNext={() => goNext(["meditation_observation"])}
          onBack={goBack}
        />
      </>,
    );
  }

  if (step === 3) {
    return stepShell(
      <>
        <TitleBlock title={t("mm.step3.title")} body={t("mm.step3.body")} />
        <div className="space-y-4">
          <div>
            <div className="label-cap mb-2">{t("mm.step3.q1")}</div>
            <Field
              testid="mm-f-remembered_experience"
              value={form.remembered_experience}
              onChange={(v) => set("remembered_experience", v)}
            />
          </div>
          <div>
            <div className="label-cap mb-2">{t("mm.step3.q2")}</div>
            <Field
              testid="mm-f-video_observation"
              value={form.video_observation}
              onChange={(v) => set("video_observation", v)}
            />
          </div>
          <div>
            <div className="label-cap mb-2">{t("mm.step3.q3")}</div>
            <Field
              testid="mm-f-video_surprise"
              value={form.video_surprise}
              onChange={(v) => set("video_surprise", v)}
            />
          </div>
        </div>
        <NavRow saving={saving} t={t}
          onNext={() =>
            goNext([
              "remembered_experience",
              "video_observation",
              "video_surprise",
            ])
          }
          onBack={goBack}
          extra={
            <button
              type="button"
              data-testid="mm-skip-step3"
              onClick={skipStep3}
              className="ghost-btn"
              disabled={saving}
            >
              {t("mm.step3.skip")}
            </button>
          }
        />
      </>,
    );
  }

  if (step === 4) {
    return stepShell(
      <>
        <TitleBlock title={t("mm.step4.title")} />
        <p className="text-stone-400 text-sm">{t("mm.step4.support")}</p>
        <Field
          testid="mm-f-what_worked"
          value={form.what_worked}
          onChange={(v) => set("what_worked", v)}
        />
        <NavRow saving={saving} t={t} onNext={() => goNext(["what_worked"])} onBack={goBack} />
      </>,
    );
  }

  if (step === 5) {
    return stepShell(
      <>
        <TitleBlock title={t("mm.step5.title")} />
        <div>
          <div className="label-cap mb-2">{t("mm.step5.question")}</div>
          <Field
            testid="mm-f-what_to_improve"
            value={form.what_to_improve}
            onChange={(v) => set("what_to_improve", v)}
          />
        </div>
        <NavRow saving={saving} t={t}
          onNext={() => goNext(["what_to_improve"])}
          onBack={goBack}
        />
      </>,
    );
  }

  if (step === 6) {
    return stepShell(
      <>
        <TitleBlock title={t("mm.step6.title")} />
        <p className="text-stone-400 text-sm whitespace-pre-line">
          {t("mm.step6.support")}
        </p>
        <Field
          testid="mm-f-lesson_learned"
          value={form.lesson_learned}
          onChange={(v) => set("lesson_learned", v)}
        />
        <NavRow saving={saving} t={t} onNext={() => goNext(["lesson_learned"])} onBack={goBack} />
      </>,
    );
  }

  if (step === 7) {
    return stepShell(
      <>
        <TitleBlock title={t("mm.step7.title")} body={t("mm.step7.explanation")} />
        <div className="paper p-4 space-y-2">
          <div className="label-cap">EXAMPLES</div>
          <ul className="space-y-2 text-sm text-stone-300 italic">
            <li>{t("mm.step7.example1")}</li>
            <li>{t("mm.step7.example2")}</li>
            <li>{t("mm.step7.example3")}</li>
            <li>{t("mm.step7.example4")}</li>
          </ul>
        </div>
        <div>
          <div className="label-cap mb-2">{t("mm.step7.q1")}</div>
          <Field
            testid="mm-f-behavior_pattern"
            value={form.behavior_pattern}
            onChange={(v) => set("behavior_pattern", v)}
          />
        </div>
        <div>
          <div className="label-cap mb-2">{t("mm.step7.q2")}</div>
          <Field
            testid="mm-f-behavior_pattern_history"
            value={form.behavior_pattern_history}
            onChange={(v) => set("behavior_pattern_history", v)}
          />
        </div>
        <NavRow saving={saving} t={t}
          onNext={() =>
            goNext(["behavior_pattern", "behavior_pattern_history"])
          }
          onBack={goBack}
        />
      </>,
    );
  }

  if (step === 8) {
    return stepShell(
      <>
        <TitleBlock title={t("mm.step8.title")} />
        <div>
          <div className="label-cap mb-2">{t("mm.step8.question")}</div>
          <Field
            testid="mm-f-current_challenge"
            value={form.current_challenge}
            onChange={(v) => set("current_challenge", v)}
          />
        </div>
        <NavRow saving={saving} t={t}
          onNext={() => goNext(["current_challenge"])}
          onBack={goBack}
        />
      </>,
    );
  }

  if (step === 9) {
    return stepShell(
      <>
        <TitleBlock title={t("mm.step9.title")} />
        {hasPreCasting ? (
          <div className="paper p-4 space-y-2" data-testid="mm-pre-reminder">
            <div className="label-cap">{t("aud.section.before")}</div>
            <ul className="space-y-2 text-sm text-stone-300">
              {PRE_FIELDS.map((k) =>
                (preCasting[k] || "").trim() ? (
                  <li key={k}>
                    <div className="mono text-[10px] text-stone-500 uppercase tracking-widest">
                      {t(`aud.pre.q${PRE_FIELDS.indexOf(k) + 1}`)}
                    </div>
                    <div className="text-stone-200 whitespace-pre-line">
                      {preCasting[k]}
                    </div>
                  </li>
                ) : null,
              )}
            </ul>
          </div>
        ) : (
          <div
            className="paper p-4 text-stone-400 text-sm"
            data-testid="mm-pre-empty"
          >
            {t("mm.step9.empty_pre_casting")}
          </div>
        )}
        <div>
          <div className="label-cap mb-2">{t("mm.step9.question")}</div>
          <p className="text-stone-400 text-sm whitespace-pre-line mb-3">
            {t("mm.step9.support")}
          </p>
          <Field
            testid="mm-f-next_casting_training"
            value={form.next_casting_training}
            onChange={(v) => set("next_casting_training", v)}
          />
        </div>
        <NavRow saving={saving} t={t}
          onNext={() => goNext(["next_casting_training"])}
          onBack={goBack}
        />
      </>,
    );
  }

  if (step === 10) {
    const rows = summaryRows([
      ["mm.summary.did_well", form.what_worked],
      ["mm.summary.differently", form.what_to_improve],
      ["mm.summary.learned", form.lesson_learned],
      ["mm.summary.pattern", form.behavior_pattern],
      ["mm.summary.challenge", form.current_challenge],
      ["mm.summary.next", form.next_casting_training],
      ["mm.summary.leaving", form.leave_behind],
    ]);
    return stepShell(
      <>
        <TitleBlock title={t("mm.step10.title")} />
        <div>
          <div className="label-cap mb-2">{t("mm.step10.question")}</div>
          <Field
            testid="mm-f-leave_behind"
            value={form.leave_behind}
            onChange={(v) => set("leave_behind", v)}
          />
        </div>
        {rows.length > 0 && (
          <div className="paper p-4 space-y-3">
            {rows.map((r) => (
              <div key={r.labelKey}>
                <div className="mono text-[10px] text-amber-500 uppercase tracking-widest">
                  {t(r.labelKey)}
                </div>
                <div className="text-stone-200 whitespace-pre-line text-sm mt-1">
                  {r.val}
                </div>
              </div>
            ))}
          </div>
        )}
        <NavRow saving={saving} t={t}
          onBack={goBack}
          onNext={closeAudition}
          nextLabel={t("mm.step10.close_btn")}
        />
      </>,
    );
  }

  // step === 11: final screen
  return stepShell(
    <>
      <TitleBlock title={t("mm.title")} />
      <div className="paper p-6 space-y-2">
        <p className="serif text-2xl text-stone-100">{t("mm.final.line1")}</p>
        <p className="text-stone-200">{t("mm.final.line2")}</p>
        <p className="text-stone-200">{t("mm.final.line3")}</p>
        <p className="text-stone-200">{t("mm.final.line4")}</p>
        <p className="text-stone-200">{t("mm.final.line5")}</p>
        <p className="mono text-amber-500 tracking-widest mt-4">
          {t("mm.final.next")}
        </p>
      </div>
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          data-testid="mm-final-back-audition"
          onClick={() => nav(`/crm/auditions/${audition.audition_id}`)}
          className="pill-btn"
        >
          {t("aud.back")}
        </button>
      </div>
    </>,
  );
}
